/**
 * IgnisLoader Component (Legacy)
 *
 * A reusable animated loader component using the Archer logo
 * Supports multiple animation styles and can be used throughout the application
 *
 * NOTE: This component is kept for backward compatibility.
 * New code should use ArcherLoader from ./ArcherLoader.tsx
 */

import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { spacing, typography } from '../../tokens';

// ============================================================================
// ANIMATION KEYFRAMES
// ============================================================================

// Pulse + Glow (Default - rests at the large size, breathes very gently above it)
const pulseAnimation = keyframes`
  0%, 100% {
    transform: scale(1.08);
    opacity: 0.92;
  }
  50% {
    transform: scale(1.14);
    opacity: 1;
  }
`;

// Explode & Collapse (Dramatic)
const explodeCollapse = keyframes`
  0%, 100% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
    filter: blur(0px) drop-shadow(0 0 20px rgba(139, 92, 246, 0.5));
  }
  25% {
    transform: scale(1.5) rotate(90deg);
    opacity: 0.3;
    filter: blur(8px) drop-shadow(0 0 60px rgba(139, 92, 246, 1));
  }
  50% {
    transform: scale(0.5) rotate(180deg);
    opacity: 0.5;
    filter: blur(4px) drop-shadow(0 0 40px rgba(236, 72, 153, 0.8));
  }
  75% {
    transform: scale(1.3) rotate(270deg);
    opacity: 0.4;
    filter: blur(6px) drop-shadow(0 0 50px rgba(59, 130, 246, 0.9));
  }
`;

// Breathing (Calm and subtle)
const breathe = keyframes`
  0%, 100% {
    transform: scale(1);
    filter: drop-shadow(0 0 15px rgba(232, 115, 74, 0.35));
  }
  50% {
    transform: scale(1.15);
    filter: drop-shadow(0 0 30px rgba(232, 115, 74, 0.6));
  }
`;

// Spin with Glow
const spinGlow = keyframes`
  0% {
    transform: rotate(0deg);
    filter: drop-shadow(0 0 20px rgba(139, 92, 246, 0.5));
  }
  50% {
    filter: drop-shadow(0 0 50px rgba(236, 72, 153, 0.8));
  }
  100% {
    transform: rotate(360deg);
    filter: drop-shadow(0 0 20px rgba(139, 92, 246, 0.5));
  }
`;

// Float & Bounce (Enhanced with more bounce)
const floatBounce = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  25% {
    transform: translateY(-30px);
  }
  50% {
    transform: translateY(-15px);
  }
  75% {
    transform: translateY(-25px);
  }
`;

// Heartbeat
const heartbeat = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  10% {
    transform: scale(1.1);
  }
  20% {
    transform: scale(1);
  }
  30% {
    transform: scale(1.15);
  }
  40% {
    transform: scale(1);
  }
`;

// Rainbow Glow
const rainbowGlow = keyframes`
  0% {
    filter: drop-shadow(0 0 30px rgba(139, 92, 246, 0.8));
  }
  25% {
    filter: drop-shadow(0 0 30px rgba(236, 72, 153, 0.8));
  }
  50% {
    filter: drop-shadow(0 0 30px rgba(59, 130, 246, 0.8));
  }
  75% {
    filter: drop-shadow(0 0 30px rgba(16, 185, 129, 0.8));
  }
  100% {
    filter: drop-shadow(0 0 30px rgba(139, 92, 246, 0.8));
  }
`;

// ============================================================================
// ANIMATION CONFIGURATIONS
// ============================================================================

export type AnimationStyle = 
  | 'pulse'
  | 'explode'
  | 'breathe'
  | 'spin'
  | 'float'
  | 'heartbeat'
  | 'rainbow';

interface AnimationConfig {
  animation: any;
  duration: string;
  easing?: string;
  imageAnimation?: any;
}

const animationConfigs: Record<AnimationStyle, AnimationConfig> = {
  pulse: {
    animation: pulseAnimation,
    duration: '3.6s',
    easing: 'linear',
    // No imageAnimation — SVG has built-in am-ringGlow and am-dotGlow filters
  },
  explode: {
    animation: explodeCollapse,
    duration: '3s',
  },
  breathe: {
    animation: breathe,
    duration: '3s',
  },
  spin: {
    animation: spinGlow,
    duration: '2s',
  },
  float: {
    animation: floatBounce,
    duration: '2s',
  },
  heartbeat: {
    animation: heartbeat,
    duration: '1.5s',
  },
  rainbow: {
    animation: rainbowGlow,
    duration: '4s',
  },
};

// ============================================================================
// COMPONENT
// ============================================================================

export interface IgnisLoaderProps {
  /** Size of the loader in pixels */
  size?: number;
  /** Animation style to use */
  animationStyle?: AnimationStyle;
  /** Optional loading message to display below the logo */
  message?: string;
  /** Whether to show the loader in fullscreen mode */
  fullscreen?: boolean;
}

export const IgnisLoader: React.FC<IgnisLoaderProps> = ({
  size = 260,
  animationStyle = 'float',
  message,
  fullscreen = false,
}) => {
  const theme = useTheme();
  const config = animationConfigs[animationStyle];

  const loaderContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: spacing.scale[3],
      }}
    >
      <Box
        sx={{
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: `${config.animation} ${config.duration} ${config.easing ?? 'ease-in-out'} infinite`,
        }}
      >
        <img
          src="/archer_logo.svg"
          alt="Archer"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            animation: config.imageAnimation
              ? `${config.imageAnimation} ${config.duration} ${config.easing ?? 'ease-in-out'} infinite`
              : undefined,
          }}
        />
      </Box>
      {message && (
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontWeight: typography.fontWeight.medium,
            fontSize: typography.fontSize.sm,
            textAlign: 'center',
            maxWidth: 300,
          }}
        >
          {message}
        </Typography>
      )}
    </Box>
  );

  if (fullscreen) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.palette.background.default,
          zIndex: 9999,
        }}
      >
        {loaderContent}
      </Box>
    );
  }

  return loaderContent;
};

export default IgnisLoader;

